import { TMkResort } from '@features/search/services/searchTypes'
import { IMkCheckboxField, IMkInputField, IMkNumberField, IMkRadioField, IMkSelectField, IMkStep, IMkTextField, MkApiContext } from '@typescript'

export enum EMkBookStepName {
  MODIFY_HUB = 'modifyHub',
  COMPLETE_BOOKING = 'completeBooking',
  PAYMENT_OPTIONS = 'paymentOptions',
  RESERVATION_BALANCE = 'reservationBalance',
  TRAVELER_INFO = 'travelerInfo',
  VIP_UPGRADES = 'vipUpgrades',
  MODIFY_TRIP_DATES = 'modifyDates',
  MODIFY_TRAVELER = 'modifyTraveler',
  MODIFY_PROTECTION = 'modifyProtection',
  MODIFY_REVIEW_PAY = 'modifyReviewPay',
  MODIFY_COMPLETE = 'modifyComplete',
  CANCEL_TRIP = 'CANCEL_TRIP',
}

export type MkSuite = {
  id?: string
  size: string
  availCount?: string
  cost: string
  name?: string
  type?: string
  description?: string
  image?: string
  clubPassFee?: string
}

export type TMkAvailableSuite = {
  id: string
  size: string
  numberOfAvailableSuites: string
  cost: string
  name: string
  type: string
  description: string
  image: string
  clubPassFee: string
}

export interface IMkBookInputField extends IMkInputField {
  suiteId?: string | undefined
  incurs?: {
    amount: number
    unit: string
  }
  badge?: {
    label: string
  }
}

export interface IMkBookSliderField extends IMkBookInputField {
  help: string | undefined
  max?: number | undefined
  min?: number | undefined
  step?: number | undefined
}

export interface IMkBookTelField extends IMkBookInputField {
  // value?: string | undefined
}

export interface IMkBookCheckboxField extends IMkCheckboxField {
  incurs?: {
    amount: number
    unit: string
  }
}

export interface IMkBookNumberField extends IMkNumberField {
  step?: number | undefined
}

export interface IMkBookRadioField extends IMkRadioField {
  incurs?: {
    amount: number
    unit: string
  }
  paymentIcons?: { alt: string; url: string }[] | undefined
}

export type IMkAnyBookField = IMkBookCheckboxField | IMkBookNumberField | IMkBookRadioField | IMkSelectField | IMkBookSliderField | IMkBookTelField | IMkTextField

export interface IMkBookStepModifyTripDates extends IMkStep {
  name: EMkBookStepName.MODIFY_TRIP_DATES
}

export interface IMkBookStepVipUpgrades extends IMkStep {
  name: EMkBookStepName.VIP_UPGRADES
}

export interface IMkBookStepTravelerInfo extends IMkStep {
  name: EMkBookStepName.TRAVELER_INFO
}

export interface IMkBookStepModifyTravelerInfo extends IMkStep {
  name: EMkBookStepName.MODIFY_TRAVELER
}

export interface IMkBookStepReservationBalance extends IMkStep {
  name: EMkBookStepName.RESERVATION_BALANCE
}

export interface IMkBookStepPaymentOptions extends IMkStep {
  name: EMkBookStepName.PAYMENT_OPTIONS
}

export interface IMkBookStepModifyPointsProtection extends IMkStep {
  name: EMkBookStepName.MODIFY_PROTECTION
}

export interface IMkConfirmationDialogContent {
  image: string
  headline: string
  text: string
}

export interface IMkBookStepCompleteBooking extends Omit<IMkStep, 'content'> {
  name: EMkBookStepName.COMPLETE_BOOKING
  content: {
    [key: string]: string | string[] | IMkConfirmationDialogContent | undefined
    confirmationDialog?: IMkConfirmationDialogContent
  }
}

export interface IMkBookStepCompleteModifyTrip extends IMkStep {
  name: EMkBookStepName.MODIFY_TRIP
}

export interface IMkBookStepReviewPay extends IMkStep {
  name: EMkBookStepName.MODIFY_REVIEW_PAY
}

export type TMkBookStep =
  | IMkBookStepModifyTripDates
  | IMkBookStepVipUpgrades
  | IMkBookStepTravelerInfo
  | IMkBookStepModifyTravelerInfo
  | IMkBookStepReservationBalance
  | IMkBookStepPaymentOptions
  | IMkBookStepModifyPointsProtection
  | IMkBookStepCompleteBooking
  | IMkBookStepCompleteModifyTrip
  | IMkBookStepReviewPay

export interface IBookRentalOptionRowHeaders {
  [key: string]: string
}

export interface IBookRentalOption extends IBookRentalOptionRowHeaders {
  key: string
  name: string
}

/** Cost amount units used in ICost and cancellation/booking flows */
export enum CostUnit {
  USD = 'USD',
  POINTS = 'POINTS',
  HOUSEKEEPING_CREDITS = 'HOUSEKEEPING_CREDITS',
}

export interface ICost {
  amount: number
  description?: string
  emphasized?: boolean
  id: string
  itemized?: ICost[]
  label: string
  showInSummary?: boolean
  unit?: string
}

export interface IBookRentalOptions {
  rowHeaders: IBookRentalOptionRowHeaders
  columns: IBookRentalOption[]
}

export type TMkBookSummaryResortAlert = {
  title?: string
  message: string
}

export type TMkBookSummaryResort = Omit<TMkResort, 'alerts'> & {
  alerts?: TMkBookSummaryResortAlert[]
}

export type TPaymentReimbursementPaymentMethod = {
  charges: ICost[]
  icon?: {
    alt: string
    url: string
  }
  paymentDetailLabel?: string
  paymentTypeLabel?: string
}

export interface IMkBookSummary {
  resortKey: string
  resortName: string
  resort?: TMkBookSummaryResort
  stay: {
    suiteType: string
    suiteId: string
    suite?: TMkAvailableSuite
    checkIn: string
    checkOut: string
    checkInDate: string
    checkOutDate: string
    numberOfNights: number
    numberOfGuests: number
    numberOfAdults: number
    numberOfChildren: number
    specialRequests?: string | undefined
    emphasizedSuite: boolean
  }
  membershipCharges: ICost[]
  offerCodeEligible: boolean
  paymentCharges: ICost[]
  reimbursements: {
    member: {}
    payment: {
      paymentMethods: TPaymentReimbursementPaymentMethod[]
      title: string
      totalToBeReimbursed: ICost
    }
  }
  travelers: IMkTraveler[]
  vipUpgrade: any
  vipUpgradeTo: any

  [key: string]: any
}

export interface IMkTripSummary extends IMkBookSummary {
  tripId: string
  tripStatus?: string
  tripType?: string
  tripStartDate?: string
  tripEndDate?: string
}

export interface IMkBookDraft {
  bookingSteps: TMkBookStep[]
  bookSummary: IMkBookSummary
  originalTripSummary?: IMkTripSummary
  errors: Array<IMkBookingError>
  meta: {
    createdAt: string
    updatedAt: string
    createdBy: string
    updatedBy: string
    status: string
    reservationId: string
    [key: string]: any
  }
  userBalances: { [key: string]: number }
}

export interface IMkBookingError extends Error {
  code?: string
  raw?: Error
  message: string
}

export interface IMkTraveler {
  age?: number
  email?: string
  firstName: string
  lastName: string
}

export interface IReservation extends IMkBookSummary {}

// IMkTraveler is already defined above

export interface IMkTripDetails extends Omit<IMkBookSummary, 'vipUpgrade' | 'vipUpgradeTo' | 'cost' | 'travelers'> {
  resortImg: {
    url: string
    description: string
  }
  resortName: string
  resortKey: string
  tripId: string
  confirmationNumber?: string
  resortInformation: {
    address: {
      street: string
      city: string
      zipCode: string
      phoneNumber: string
    }
    alerts: string[]
  }
  stay: {
    checkIn: string
    checkOut: string
    suiteType: string
    suiteId: string
    numberOfNights: number
    numberOfGuests: number
    numberOfAdults: number
    numberOfChildren: number
    emphasizedSuite: boolean
  }
  helpfulHints: string[]
  cancellationDetails: string
  isPastTrip?: boolean
}

export interface IMkBookTimerData {
  startTime: number // timestamp when timer started
  durationMinutes: number // total duration in minutes
  remainingSeconds: number // remaining time in seconds
  isLastTwoMinutes: boolean // flag for last two minutes warning
  isTimeUp: boolean // flag for when time is up
  stopped: boolean // flag for whether timer is stopped
}

export interface IMkBookState {
  bookDraft: IMkBookDraft | undefined
  bookingErrors?: IMkBookingError[]
  isLoading: boolean
  timerData: IMkBookTimerData | undefined
  originalTripSummary?: IMkTripSummary
  isModifying?: boolean
  apiContext: MkApiContext
  requiresAdditionalPayment?: boolean
  hasUpdatedModifySteps?: boolean
}

export enum EMkBookAction {
  FETCH_DRAFT = 'book/FETCH_DRAFT',
  SET_BOOK_DRAFT = 'book/SET_BOOK_DRAFT',
  SET_CURRENT_STEP = 'book/SET_CURRENT_STEP',
  UPDATE_FIELD = 'book/UPDATE_FIELD',
  SET_ERROR = 'book/SET_ERROR',
  CLEAR_ERROR = 'book/CLEAR_ERROR',
  SET_LOADING = 'book/SET_LOADING',
  INIT_BOOKING = 'book/INIT_BOOKING',
  SET_TIMER_DATA = 'book/SET_TIMER_DATA',
  CLEAR_TIMER_DATA = 'book/CLEAR_TIMER_DATA',
  SET_TIMER_RUNNING = 'book/SET_TIMER_RUNNING',
  CLEAR_TIMER_RUNNING = 'book/CLEAR_TIMER_RUNNING',
  SET_TIMER_STOPPED = 'book/SET_TIMER_STOPPED',
  SET_MODIFY_CONTEXT = 'modify/SET_MODIFY_CONTEXT',
  SET_ORIGINAL_RESERVATION = 'modify/SET_ORIGINAL_RESERVATION',
  SET_API_CONTEXT = 'SET_API_CONTEXT',
  SET_ORIGINAL_TRIP_SUMMARY = 'SET_ORIGINAL_TRIP_SUMMARY',
  SET_REQUIRES_ADDITIONAL_PAYMENT = 'book/SET_REQUIRES_ADDITIONAL_PAYMENT',
  UPDATE_DATES_STEP_CONTENT = 'book/UPDATE_DATES_STEP_CONTENT',
  SET_HAS_UPDATED_MODIFY_STEPS = 'book/SET_HAS_UPDATED_MODIFY_STEPS',
}

// Action Interfaces
interface InitBookingAction {
  type: typeof EMkBookAction.INIT_BOOKING
  payload: { resort: Partial<TMkResort>; suite: TMkAvailableSuite; checkInDate: string; checkOutDate: string }
}

interface SetBookingDraftAction {
  type: typeof EMkBookAction.SET_BOOK_DRAFT
  payload: IMkBookDraft
}

interface UpdateFieldAction {
  type: typeof EMkBookAction.UPDATE_FIELD
  payload: {
    stepName: EMkBookStepName
    fieldName: string
    fieldValue: IMkBookInputField['value']
    fieldType: string
  }
}

interface SetErrorAction {
  type: typeof EMkBookAction.SET_ERROR
  payload: IMkBookingError
}

interface ClearErrorAction {
  type: typeof EMkBookAction.CLEAR_ERROR
}

interface SetLoadingAction {
  type: typeof EMkBookAction.SET_LOADING
  payload: boolean
}

interface ClearBookingErrorAction {
  type: typeof EMkBookAction.CLEAR_ERROR
}

interface SetTimerDataAction {
  type: typeof EMkBookAction.SET_TIMER_DATA
  payload: IMkBookTimerData
}

interface ClearTimerDataAction {
  type: typeof EMkBookAction.CLEAR_TIMER_DATA
}

interface SetTimerRunningAction {
  type: typeof EMkBookAction.SET_TIMER_RUNNING
  payload: boolean
}

interface ClearTimerRunningAction {
  type: typeof EMkBookAction.CLEAR_TIMER_RUNNING
  payload: boolean
}

interface SetTimerStoppedAction {
  type: typeof EMkBookAction.SET_TIMER_STOPPED
  payload: boolean
}

interface SetOriginalTripSummaryAction {
  type: typeof EMkBookAction.SET_ORIGINAL_TRIP_SUMMARY
  payload: IMkTripSummary
}

interface SetApiContextAction {
  type: typeof EMkBookAction.SET_API_CONTEXT
  payload: MkApiContext
}

interface SetRequiresAdditionalPaymentAction {
  type: typeof EMkBookAction.SET_REQUIRES_ADDITIONAL_PAYMENT
  payload: boolean
}

export interface UpdateDatesStepContentPayload {
  earliestCheckIn?: string
  latestCheckOut?: string
  excluded?: string[]
}

interface UpdateDatesStepContentAction {
  type: typeof EMkBookAction.UPDATE_DATES_STEP_CONTENT
  payload: UpdateDatesStepContentPayload
}

interface SetHasUpdatedModifyStepsAction {
  type: typeof EMkBookAction.SET_HAS_UPDATED_MODIFY_STEPS
  payload: boolean
}

export type MkBookAction =
  | ClearErrorAction
  | ClearBookingErrorAction
  | ClearTimerDataAction
  | ClearTimerRunningAction
  | InitBookingAction
  | SetTimerStoppedAction
  | SetApiContextAction
  | SetBookingDraftAction
  | SetErrorAction
  | SetLoadingAction
  | SetOriginalTripSummaryAction
  | SetTimerDataAction
  | SetTimerRunningAction
  | SetRequiresAdditionalPaymentAction
  | UpdateFieldAction
  | UpdateDatesStepContentAction
  | SetHasUpdatedModifyStepsAction

export enum EMkModifyBookStep {
  DATES = 'dates',
  TRAVELER = 'traveler',
  PROTECTION = 'protection',
}

export type MkModifyBookStep = EMkModifyBookStep.DATES | EMkModifyBookStep.TRAVELER | EMkModifyBookStep.PROTECTION
